import { logger, LogContext, trackEvent } from './logger'

// hides the overlay and reloads the page, used when the overlay crashes and on a schedule to clear long running state

const HIDDEN_CLASS = 'overlay--reloading'
// matches the fade out on .overlay--reloading in app.css
const FADE_OUT_MS = 600

const CRASH_LOG_KEY = 'overlay-crash-reloads'
const CRASH_WINDOW_MS = 10 * 60 * 1000
const CRASH_BASE_DELAY_MS = 5000
const CRASH_MAX_DELAY_MS = 5 * 60 * 1000

export type ReloadReason = 'crash' | 'scheduled'

let reloadTimeout: ReturnType<typeof setTimeout> | null = null

// session storage can throw in locked down browser sources, the reload still has to happen without it
const readCrashLog = (): number[] => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(CRASH_LOG_KEY) || '[]')
    return Array.isArray(parsed) ? parsed.filter((timestamp) => typeof timestamp === 'number') : []
  } catch {
    return []
  }
}

const writeCrashLog = (timestamps: number[]) => {
  try {
    sessionStorage.setItem(CRASH_LOG_KEY, JSON.stringify(timestamps))
  } catch {
    // nothing to do, we just lose the backoff
  }
}

// back off when crashes keep repeating so a persistent fault doesn't reload the source in a tight loop
const getCrashReloadDelay = (): number => {
  const now = Date.now()
  const recentCrashes = readCrashLog().filter((timestamp) => now - timestamp < CRASH_WINDOW_MS)
  writeCrashLog([...recentCrashes, now])
  return Math.min(CRASH_BASE_DELAY_MS * 2 ** recentCrashes.length, CRASH_MAX_DELAY_MS)
}

export const hideOverlay = () => {
  document.documentElement.classList.add(HIDDEN_CLASS)
}

export const isReloadScheduled = () => reloadTimeout !== null

export const reloadPage = (reason: ReloadReason, error?: unknown) => {
  // the first failure wins, later errors while we wait to reload shouldn't push the reload back
  if (reloadTimeout !== null) return

  hideOverlay()

  const delay = reason === 'crash' ? getCrashReloadDelay() : FADE_OUT_MS
  // the crash itself is reported by faro's error capture or the error boundary, this records the recovery
  trackEvent('overlay_reload', { reason, delayMs: delay })
  if (reason === 'crash') logger.warn(LogContext.OVERLAY, 'Reloading after crash', { delayMs: delay }, error)
  else logger.info(LogContext.OVERLAY, 'Scheduled refresh', { uptimeMs: Math.round(performance.now()) })

  reloadTimeout = setTimeout(() => window.location.reload(), delay)
}
